/**
 * DMA (Designated Market Area) Utilities
 *
 * Provides runtime resolution of DMA names to IDs for filtering.
 * Caches DMA list for performance.
 */

export interface DmaRecord {
  id: number;
  name: string;
  dmaCode: string;
  rank?: number | null;
  states?: string[] | null;
  population?: number | null;
}

// In-memory cache for DMA list
let dmaCache: DmaRecord[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all DMAs from the API (with caching)
 */
export async function fetchDmas(): Promise<DmaRecord[]> {
  const now = Date.now();

  // Return cached data if still valid
  if (dmaCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    return dmaCache;
  }

  try {
    const response = await fetch('/api/dmas');
    if (!response.ok) {
      throw new Error(`Failed to fetch DMAs: ${response.status}`);
    }

    const dmas = await response.json();
    dmaCache = dmas;
    cacheTimestamp = now;

    return dmas;
  } catch (error) {
    console.error('[DMA Utils] Error fetching DMAs:', error);

    // Return cached data even if expired, as fallback
    if (dmaCache) {
      console.warn('[DMA Utils] Using expired cache due to fetch error');
      return dmaCache;
    }

    throw error;
  }
}

/**
 * Clear the DMA cache (useful for testing or forced refresh)
 */
export function clearDmaCache(): void {
  dmaCache = null;
  cacheTimestamp = null;
}

/**
 * Normalize DMA name for fuzzy matching
 * - Lowercase
 * - Remove extra whitespace
 * - Remove common punctuation
 */
function normalizeDmaName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Find a single DMA by exact or fuzzy name match
 *
 * @param name - DMA name to search for
 * @param dmas - Optional pre-fetched DMA list (avoids re-fetching)
 * @returns DMA record or undefined if not found
 */
export async function findDmaByName(
  name: string,
  dmas?: DmaRecord[]
): Promise<DmaRecord | undefined> {
  const dmaList = dmas || await fetchDmas();
  const normalizedSearch = normalizeDmaName(name);

  // Try exact match first
  const exactMatch = dmaList.find(
    dma => normalizeDmaName(dma.name) === normalizedSearch
  );

  if (exactMatch) return exactMatch;

  // Try fuzzy match (contains)
  const fuzzyMatch = dmaList.find(
    dma => normalizeDmaName(dma.name).includes(normalizedSearch) ||
           normalizedSearch.includes(normalizeDmaName(dma.name))
  );

  return fuzzyMatch;
}

/**
 * Resolve DMA names to IDs
 *
 * @param names - Array of DMA names to resolve
 * @returns Array of DMA IDs (only successful matches)
 *
 * @example
 * const ids = await resolveDmaIds(['Dallas/Fort Worth', 'New York']);
 * // Returns: [5, 1] (assuming those are the IDs)
 */
export async function resolveDmaIds(names: string[]): Promise<number[]> {
  if (!names || names.length === 0) return [];

  const dmas = await fetchDmas();
  const ids: number[] = [];
  const unmatched: string[] = [];

  for (const name of names) {
    const dma = await findDmaByName(name, dmas);
    if (dma) {
      ids.push(dma.id);
    } else {
      unmatched.push(name);
    }
  }

  // Log warnings for unmatched DMAs
  if (unmatched.length > 0) {
    console.warn('[DMA Utils] Could not resolve DMA names to IDs:', unmatched);
  }

  return ids;
}

/**
 * Resolve DMA IDs to names
 *
 * @param ids - Array of DMA IDs to resolve
 * @returns Array of DMA names (only successful matches)
 *
 * @example
 * const names = await resolveDmaNames([5, 1]);
 * // Returns: ['Dallas/Fort Worth', 'New York']
 */
export async function resolveDmaNames(ids: number[]): Promise<string[]> {
  if (!ids || ids.length === 0) return [];

  const dmas = await fetchDmas();
  const names: string[] = [];
  const unmatched: number[] = [];

  for (const id of ids) {
    const dma = dmas.find(d => d.id === id);
    if (dma) {
      names.push(dma.name);
    } else {
      unmatched.push(id);
    }
  }

  // Log warnings for unmatched IDs
  if (unmatched.length > 0) {
    console.warn('[DMA Utils] Could not resolve DMA IDs to names:', unmatched);
  }

  return names;
}

/**
 * Get a DMA by ID
 *
 * @param id - DMA ID to fetch
 * @returns DMA record or undefined if not found
 */
export async function getDmaById(id: number): Promise<DmaRecord | undefined> {
  const dmas = await fetchDmas();
  return dmas.find(dma => dma.id === id);
}

/**
 * Validate that all DMA names exist in the database
 *
 * @param names - Array of DMA names to validate
 * @returns Object with valid names, invalid names, and resolved IDs
 */
export async function validateDmaNames(names: string[]): Promise<{
  valid: string[];
  invalid: string[];
  resolvedIds: number[];
}> {
  if (!names || names.length === 0) {
    return { valid: [], invalid: [], resolvedIds: [] };
  }

  const dmas = await fetchDmas();
  const valid: string[] = [];
  const invalid: string[] = [];
  const resolvedIds: number[] = [];

  for (const name of names) {
    const dma = await findDmaByName(name, dmas);
    if (dma) {
      valid.push(name);
      resolvedIds.push(dma.id);
    } else {
      invalid.push(name);
    }
  }

  return { valid, invalid, resolvedIds };
}
